import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {{ builds?: Array<Record<string, string>>, adventures?: Array<Record<string, string>> } | null}
   */
  #metaobjectSearchData = null;

  /**
   * @type {Promise<Array<Record<string, string>>> | null}
   */
  #storefrontBuildsPromise = null;

  #storefrontBuildsLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  #getMetaobjectSearchData() {
    if (this.#metaobjectSearchData) return this.#metaobjectSearchData;

    const source = this.querySelector('[data-predictive-search-metaobjects]');
    if (!(source instanceof HTMLScriptElement) || !source.textContent) {
      this.#metaobjectSearchData = {};
      return this.#metaobjectSearchData;
    }

    try {
      this.#metaobjectSearchData = JSON.parse(source.textContent);
    } catch {
      this.#metaobjectSearchData = {};
    }

    return this.#metaobjectSearchData;
  }

  #getBuildsApiConfig() {
    const token = this.dataset.buildsStorefrontApiToken?.trim();
    if (!token) return null;

    const version = this.dataset.buildsStorefrontApiVersion || '2026-01';
    const root = this.dataset.buildsPageRoot || '/';

    return {
      token,
      version,
      buildsBasePath: `${root.replace(/\/$/, '')}/pages/builds/`,
    };
  }

  /**
   * @param {Array<Record<string, any>>} fields
   * @param {string} key
   */
  #findBuildField(fields, key) {
    return fields.find((field) => field?.key === key);
  }

  /**
   * @param {Array<Record<string, any>>} fields
   * @param {string} key
   */
  #getBuildFieldValue(fields, key) {
    return this.#findBuildField(fields, key)?.value?.toString().trim() || '';
  }

  /**
   * @param {string} value
   */
  #cleanSearchText(value) {
    return String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/[{}[\]",:]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * @param {Record<string, any> | undefined} field
   */
  #parseBuildFieldImage(field) {
    const reference = field?.reference;
    if (reference?.__typename === 'MediaImage' && reference.image?.url) {
      return reference.image;
    }
    if (reference?.__typename === 'GenericFile' && reference.url) {
      return { url: reference.url };
    }

    const node = field?.references?.nodes?.[0];
    if (node?.__typename === 'MediaImage' && node.image?.url) {
      return node.image;
    }
    if (node?.__typename === 'GenericFile' && node.url) {
      return { url: node.url };
    }

    return null;
  }

  /**
   * @param {Record<string, any>} node
   * @param {string} buildsBasePath
   */
  #buildSearchItemFromStorefront(node, buildsBasePath) {
    const fields = Array.isArray(node.fields) ? node.fields : [];
    const handle = node.handle || '';
    const url = handle ? `${buildsBasePath}${handle}` : '';
    if (!url) return null;

    const title = this.#getBuildFieldValue(fields, 'build_title') || this.#getBuildFieldValue(fields, 'build_name') || handle;
    const label = this.#getBuildFieldValue(fields, 'build_name') || title;
    const color = this.#getBuildFieldValue(fields, 'color_name') || this.#getBuildFieldValue(fields, 'customization_body_color');
    const drivetrain = this.#getBuildFieldValue(fields, 'specs_drivetrain');
    const description = this.#cleanSearchText(this.#getBuildFieldValue(fields, 'description'));
    const excerpt = description.split(/\s+/).filter(Boolean).slice(0, 12).join(' ');
    const image =
      this.#parseBuildFieldImage(this.#findBuildField(fields, 'side_image')) ||
      this.#parseBuildFieldImage(this.#findBuildField(fields, 'hero_image')) ||
      this.#parseBuildFieldImage(this.#findBuildField(fields, 'specs_image'));

    return {
      type: 'build',
      title,
      eyebrow: label,
      excerpt,
      url,
      image: image?.url || '',
      imageAlt: image?.altText || title,
      search: [label, title, handle, color, drivetrain, description].filter(Boolean).join(' '),
    };
  }

  #loadStorefrontBuilds() {
    if (this.#storefrontBuildsLoaded || this.#storefrontBuildsPromise) {
      return this.#storefrontBuildsPromise || Promise.resolve(this.#getMetaobjectSearchData().builds || []);
    }

    const config = this.#getBuildsApiConfig();
    if (!config) return Promise.resolve(this.#getMetaobjectSearchData().builds || []);

    const data = this.#getMetaobjectSearchData();
    const existingBuilds = data.builds || [];
    const seenUrls = new Set(existingBuilds.map((build) => build.url).filter(Boolean));
    const apiUrl = `${window.location.origin}/api/${config.version}/graphql.json`;
    const query = `
      query SearchBuilds($first: Int!, $after: String) {
        metaobjects(type: "build", sortKey: "id", reverse: true, first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              handle
              fields {
                key
                value
                reference {
                  __typename
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                  ... on GenericFile {
                    url
                  }
                }
                references(first: 1) {
                  nodes {
                    __typename
                    ... on MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                    ... on GenericFile {
                      url
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    this.#storefrontBuildsPromise = (async () => {
      let hasNextPage = true;
      let after = null;
      const apiBuilds = [];

      for (let page = 0; hasNextPage && page < 20; page += 1) {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.token,
            Accept: 'application/json',
          },
          body: JSON.stringify({
            query,
            variables: {
              first: 50,
              after,
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`Storefront API request failed: ${response.status}`);
        }

        const payload = await response.json();
        const edges = payload?.data?.metaobjects?.edges || [];
        const pageInfo = payload?.data?.metaobjects?.pageInfo;

        edges.forEach((edge) => {
          const item = this.#buildSearchItemFromStorefront(edge.node, config.buildsBasePath);
          if (!item || seenUrls.has(item.url)) return;

          seenUrls.add(item.url);
          apiBuilds.push(item);
        });

        hasNextPage = Boolean(pageInfo?.hasNextPage);
        after = pageInfo?.endCursor || null;
      }

      data.builds = [...existingBuilds, ...apiBuilds];
      this.#storefrontBuildsLoaded = true;
      return data.builds;
    })().catch((error) => {
      console.warn('Build search Storefront API fetch failed', error);
      this.#storefrontBuildsLoaded = true;
      return existingBuilds;
    });

    return this.#storefrontBuildsPromise;
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    const entityMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return String(value || '').replace(/[&<>"']/g, (character) => entityMap[character]);
  }

  /**
   * @param {Record<string, string>} item
   */
  #buildMetaobjectCard(item) {
    const type = item.type === 'adventure' ? 'adventure' : 'build';
    const mediaModifier = type === 'adventure' ? 'adventure' : 'build';
    const image = item.image
      ? `<img class="predictive-search-results__meta-card-image" src="${this.#escapeHtml(item.image)}" alt="${this.#escapeHtml(item.imageAlt || item.title)}" loading="lazy">`
      : type === 'build'
        ? '<div class="placeholder-image predictive-search-results__meta-card-image" aria-hidden="true"></div>'
        : '';
    const eyebrow =
      item.eyebrow && item.eyebrow !== item.title
        ? `<p class="predictive-search-results__meta-card-eyebrow">${this.#escapeHtml(item.eyebrow)}</p>`
        : '';
    const excerpt = item.excerpt
      ? `<p class="predictive-search-results__meta-card-subtext">${this.#escapeHtml(item.excerpt)}</p>`
      : '';

    return `
      <li class="predictive-search-results__card predictive-search-results__card--${type} predictive-search-results__card--metaobject" ref="resultsItems[]">
        <article class="predictive-search-results__meta-card">
          <a class="predictive-search-results__meta-card-link" href="${this.#escapeHtml(item.url)}">
            <span class="visually-hidden">${this.#escapeHtml(item.title)}</span>
          </a>
          <div class="predictive-search-results__meta-card-media predictive-search-results__meta-card-media--${mediaModifier}">
            ${image}
          </div>
          <div class="predictive-search-results__meta-card-content">
            ${eyebrow}
            <p class="predictive-search-results__meta-card-title">${this.#escapeHtml(item.title)}</p>
            ${excerpt}
          </div>
        </article>
      </li>
    `;
  }

  /**
   * Adds local Build and Adventure metaobject matches to native predictive search results.
   * @param {string} searchTerm
   */
  #appendMetaobjectResults(searchTerm) {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    if (!normalizedTerm) return;

    const data = this.#getMetaobjectSearchData();
    const groups = [
      { key: 'builds', label: 'Builds', target: 'builds', items: data.builds || [] },
      { key: 'adventures', label: 'Adventures', target: 'adventures', items: data.adventures || [] },
    ];

    const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!(inner instanceof HTMLElement)) return;

    inner.querySelectorAll('[data-metaobject-search-results]').forEach((element) => element.remove());

    const existingGroups = new Set(
      Array.from(inner.querySelectorAll('[data-results-group]:not([data-metaobject-search-results])'))
        .map((element) => (element instanceof HTMLElement ? element.dataset.resultsGroup : ''))
        .filter(Boolean)
    );
    let metaobjectMatchesCount = 0;

    const sections = groups
      .map((group) => {
        const matches = group.items.filter((item) => {
          return String(item.search || '').toLowerCase().includes(normalizedTerm);
        });

        if (matches.length === 0 || existingGroups.has(group.target)) return '';

        metaobjectMatchesCount += matches.length;

        return `
          <section class="predictive-search-results__group" data-results-group="${group.target}" data-metaobject-search-results="${group.key}">
            <div class="predictive-search-results__resource-header">
              <h4 class="predictive-search-results__title">${group.label}</h4>
            </div>
            <ul class="predictive-search-results__list predictive-search-results__wrapper-products predictive-search-results__wrapper-products--${group.target} list-unstyled" role="list">
              ${matches.slice(0, 6).map((item) => this.#buildMetaobjectCard(item)).join('')}
            </ul>
          </section>
        `;
      })
      .filter(Boolean);

    if (sections.length === 0) return;

    inner.querySelector('.predictive-search-results__no-results')?.remove();
    const status = inner.querySelector('[role="status"]');
    if (status instanceof HTMLElement) {
      status.textContent = `${metaobjectMatchesCount} search result${metaobjectMatchesCount === 1 ? '' : 's'} found for "${searchTerm}"`;
    }

    const insertionPoint =
      inner.querySelector('[data-results-group="products"]') ||
      inner.querySelector('[data-results-group="info"]') ||
      inner.querySelector('[data-single-result-url]');

    const template = document.createElement('template');
    template.innerHTML = sections.join('');

    if (insertionPoint) {
      insertionPoint.before(template.content);
    } else {
      inner.append(template.content);
    }
  }

  /**
   * Scroll to a grouped result category when segment controls are shown.
   * @param {Event} event - The click event.
   */
  scrollToResultsGroup(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const group = button.dataset.psGroupTarget;
    if (!group) return;

    this.querySelectorAll('[data-ps-group-target]').forEach((segment) => {
      if (!(segment instanceof HTMLElement)) return;

      const isActive = segment.dataset.psGroupTarget === group;
      segment.classList.toggle('is-active', isActive);
      segment.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.querySelector(`[data-results-group="${group}"]`)?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    if (event.type !== 'input') return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,page,article,collection,query');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;
        if (this.refs.searchInput.value.trim() !== searchTerm) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#appendMetaobjectResults(searchTerm);
        void this.#loadStorefrontBuilds().then(() => {
          const currentTerm = this.refs.searchInput.value.trim();
          if (currentTerm === searchTerm && !abortController.signal.aborted) {
            this.#appendMetaobjectResults(searchTerm);
          }
        });

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #renderEmptySearchResults() {
    const emptyResults = document.createElement('div');
    emptyResults.id = 'predictive-search-results';
    emptyResults.className = 'predictive-search-dropdown';
    emptyResults.setAttribute('role', 'listbox');
    emptyResults.setAttribute('aria-expanded', 'false');
    emptyResults.style.setProperty('--color-shadow', 'rgb(var(--color-foreground-rgb) / 0.1)');

    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.refs.predictiveSearchResults.replaceChildren(emptyResults);
  }

  #resetSearch = async () => {
    const { searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#createAbortController();
    this.#renderEmptySearchResults();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
